import useTranslation from 'next-translate/useTranslation';
import React, { useState, useRef, useEffect } from 'react';

import { createPOrder, updatePOrderZipcode } from '@/api/so';
import Button from '@/components/common/Button';
import { WeeeIcon } from '@/components/common/Icon';
import Constants from '@/constants/const';
import { getZipcode } from '@/utils/business';
import logger from '@/utils/logger';
import { getCookie, setCookie } from '@/utils/tool';
import LOGO from '@/assets/images/logo/weee-logo-black.svg';
import EMAIL from './images/send-mail.png';

import styles from './index.module.scss';
import MaterialDesignInput from '@/components/common/MaterialDesignInput';
import Popup from '@/components/common/Popup';
import Img from '@/components/common/Img';
import { setRemindEmail } from '@/api/customer';
import { Toast, removeToast } from '@/components/common/CommonToast';
import classNames from 'classnames';
import Trans from 'next-translate/Trans';
import Tracker from '@/tracks/tracker';
import useClickActionT2 from '@/tracks/useClickActionT2';
import { syncZipcodeAndStore } from '@/utils/syncZipcodeAndStore';
import { CaratRight } from '@weee-fe/kunlunicon';
import { twMerge } from 'tailwind-merge';

export const CollectEmailPop = props => {
  const { t } = useTranslation('common');
  const [email, setEmail] = useState('');
  const [err, setErr] = useState('');
  const sendEmail = async () => {
    const res = await setRemindEmail({ zipcode: props.zipcode, email: email });
    if (res.result) {
      Toast({ showIcon: false, text: t('invite_friends_send_email_success') });
      props.onCancel?.();
    } else if (res?.message) {
      setErr(res?.message);
    }
  };

  if (!props?.visible) return null;

  return (
    <Popup
      {...props}
      popupId="collect-email"
      position="top"
      animationType="slide-down"
      show={props?.visible}
      wrapperClassName={styles.emailModalContainer}>
      <div>
        <WeeeIcon
          type="iconBack"
          className={styles.back}
          onClick={() => {
            props.onCancel?.();
          }}
        />
        <Img src={LOGO} className={styles.logo} />
      </div>
      <div className={styles.CollectEmailContent}>
        <Img src={EMAIL} className={styles.mail} />
        <div className={styles.tipTitle}>
          <Trans
            i18nKey={'common:invite_friends_popup_title'}
            values={{
              zipcode: props?.zipcode
            }}
          />
        </div>
        <div className={styles.tipContent}>{t('invite_friends_popup_subtitle')}</div>
        <MaterialDesignInput
          width="100%"
          className={classNames(styles.input, { [styles.hasError]: !!err })}
          value={email}
          hasError={!!err}
          type="text"
          label={'Email'}
          onChange={v => {
            setErr('');
            setEmail(v);
          }}
        />
        {err && <div className={styles.errorMsg}>{err}</div>}
        <Button className={styles.sendbtn} type={'primary'} onClick={sendEmail}>
          {t('invite_friends_popup_btn')}
        </Button>
      </div>
    </Popup>
  );
};

export default function ZipcodeModule({ onSuccess, amount = 0 }) {
  const { t, lang } = useTranslation('common');
  const [zipcode, setZipcode] = useState(getCookie(Constants.ZIP_CODE) || '');
  const [loading, setLoading] = useState<boolean>(false);
  const [err, setErr] = useState(Boolean);
  const [showFloatingButton, setShowFloatingButton] = useState(false);
  const { buttonClickView } = useClickActionT2();
  const buttonRef = useRef(null);

  // 滚动到zipcode输入框的函数
  const scrollToZipcodeInput = () => {
    if (!showFloatingButton) return;
    const zipcodeInput = document.getElementById('zipcode-input');
    if (zipcodeInput) {
      zipcodeInput.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      // 聚焦到输入框
      zipcodeInput.focus();
    }
  };

  // 监听滚动事件，控制悬浮按钮的显示
  useEffect(() => {
    const handleScroll = () => {
      if (buttonRef.current) {
        !showFloatingButton && removeToast();
        const buttonRect = buttonRef.current.getBoundingClientRect();
        // 当按钮不在可视区域内时显示悬浮按钮
        const isButtonVisible = buttonRect.bottom > 0 && buttonRect.top < window.innerHeight;
        setShowFloatingButton(!isButtonVisible);
      }
    };

    const timer = setTimeout(() => {
      if (buttonRef.current) {
        window.addEventListener('scroll', handleScroll);
        handleScroll();
      }
    }, 1000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, [showFloatingButton]);

  const clickTrack = () => {
    buttonClickView({
      co: {
        target_nm: 'continue'
      },
      ctx: {
        step: 1,
        zipcode_before: getCookie(Constants.ZIP_CODE) || '', // 修改前
        zipcode_after: zipcode // 修改后
      }
    });
  };

  const handleSubmit = async () => {
    if (!zipcode || isNaN(+zipcode) || zipcode.length !== 5) {
      Toast({
        showIcon: false,
        innerClassName: 'rounded-600',
        textClassName: 'w-full',
        className: 'bottom-40',
        text: (
          <div
            className="w-full flex items-center justify-between cursor-pointer"
            onClick={() => {
              scrollToZipcodeInput();
            }}>
            <span className="flex-1">{t('referral_zipcode_toast')}</span>
            <CaratRight fontSize={20} />
          </div>
        )
      });
      return;
    }
    clickTrack();
    setErr(false);
    setLoading(true);
    try {
      let zopcodeInCookie = getZipcode();
      const res = await (zopcodeInCookie ? syncZipcodeAndStore({ zipcode }) : createPOrder(zipcode));
      if (res.result) {
        setCookie(Constants.ZIP_CODE, zipcode);
        onSuccess?.();
      } else {
        setErr(true);
      }
    } catch (error) {
      logger.error({
        page: window.location.href,
        method: 'updatePOrderZipcode',
        error: error
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="px-5 py-0">
        <MaterialDesignInput
          width="100%"
          className={twMerge(styles.input, '!border-primary-2')}
          value={zipcode}
          type="text"
          label={t('add_card_zipcode')}
          onChange={v => {
            setErr(false);
            if (v?.length > 5) return;
            setZipcode(v);
          }}
          onPressEnter={handleSubmit}
          id="zipcode-input"
        />
        <Button
          dataTestId="btn-redeme"
          size="large"
          loading={loading}
          onClick={handleSubmit}
          type="primary"
          bordered={false}
          className="mt-[15px] cursor-pointer">
          <span dangerouslySetInnerHTML={{ __html: t('referral_redeem_offer_btn', { amount }) }}></span>
        </Button>
        <div ref={buttonRef}></div>
      </div>

      {/* 悬浮按钮 */}
      {showFloatingButton && (
        <div className="fixed bottom-24 left-0 right-0 max-w-[350px] mx-auto z-1000">
          <Button
            dataTestId="btn-float-redeme"
            size="large"
            loading={loading}
            onClick={handleSubmit}
            type="primary"
            bordered={false}
            className="cursor-pointer">
            <span dangerouslySetInnerHTML={{ __html: t('referral_redeem_offer_btn', { amount }) }}></span>
          </Button>
        </div>
      )}

      {err && <CollectEmailPop visible={true} zipcode={zipcode} />}
    </>
  );
}
